import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ListChecks, GraduationCap, FileText, Clock, CheckCircle2, Circle, Trash2, Plus, ExternalLink, ShieldCheck } from "lucide-react";
import { PageHeader, Panel, Badge, Select, fmtD, fmtDT } from "@/components/admin/ui";
import { UploadButton } from "./LessonEditor";

const TABS = [["tasks", "Tasks", ListChecks], ["training", "Training", GraduationCap], ["documents", "Documents", FileText], ["timeclock", "Time Clock", Clock]];
const DOC_TYPES = [["servsafe", "ServSafe Certificate"], ["food_handler", "Food Handler Card"], ["id", "Photo ID"], ["w4", "W-4"], ["i9", "I-9"], ["direct_deposit", "Direct Deposit"], ["work_agreement", "Work Agreement"], ["other", "Other"]];

function TasksTab({ d, storeId, reload }) {
  const [f, setF] = useState({ title: "", due_date: "", priority: "normal" });
  const add = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    if (!storeId) return toast.error("Employee has no store assigned");
    try { await api.post(`/stores/${storeId}/tasks`, { ...f, assigned_to: d.employee.id, due_date: f.due_date || null }); toast.success("Task assigned"); setF({ title: "", due_date: "", priority: "normal" }); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (t) => { try { await api.patch(`/tasks/${t.id}`, { status: t.status === "open" ? "done" : "open" }); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async (t) => { if (!window.confirm("Delete task?")) return; try { await api.delete(`/tasks/${t.id}`); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <>
      <Panel className="p-4 mb-4 flex flex-wrap items-end gap-2" data-testid="emp-task-form">
        <div className="flex-1 min-w-[200px]"><Label>New task</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Deep clean rice station" data-testid="emp-task-title"/></div>
        <div><Label>Due</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="emp-task-due"/></div>
        <div><Label>Priority</Label><Select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></Select></div>
        <Button onClick={add} className="rounded-xl bg-[hsl(var(--primary))]" data-testid="emp-task-add"><Plus className="w-4 h-4 mr-1"/>Assign</Button>
      </Panel>
      <Panel className="divide-y divide-neutral-100">
        {d.tasks.length === 0 && <p className="p-8 text-center text-sm text-neutral-500" data-testid="emp-tasks-empty">No tasks assigned.</p>}
        {d.tasks.map((t) => (
          <div key={t.id} className="p-4 flex items-center gap-3" data-testid={`emp-task-${t.id}`}>
            <button onClick={() => toggle(t)}>{t.status === "done" ? <CheckCircle2 className="w-6 h-6 text-emerald-500"/> : <Circle className="w-6 h-6 text-neutral-300"/>}</button>
            <div className="flex-1"><div className={`font-medium ${t.status === "done" ? "line-through text-neutral-400" : ""}`}>{t.title}</div>
              {t.due_date && <div className={`text-xs ${t.overdue ? "text-red-600 font-semibold" : "text-neutral-500"}`}>Due {fmtD(t.due_date)}{t.overdue ? " · overdue" : ""}</div>}</div>
            <Badge>{t.priority}</Badge>
            <button onClick={() => remove(t)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </Panel>
    </>
  );
}

function TrainingTab({ d }) {
  const T = d.training;
  const cats = [...new Set(T.lessons.map((l) => l.category))];
  return (
    <Panel className="p-5" data-testid="emp-training">
      <div className="flex items-center gap-4 mb-5">
        <div className="flex-1 h-3 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${T.percent}%` }}/></div>
        <div className="font-display font-bold text-xl" data-testid="emp-training-percent">{T.percent}%</div>
        <div className="text-xs text-neutral-500">{T.completed}/{T.total} completed</div>
      </div>
      {cats.map((c) => (
        <div key={c} className="mb-4">
          <h3 className="text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{c}</h3>
          <ul className="divide-y divide-neutral-100">
            {T.lessons.filter((l) => l.category === c).map((l) => (
              <li key={l.id} className="py-2 flex items-center justify-between text-sm" data-testid={`emp-lesson-${l.id}`}>
                <span className="flex items-center gap-2">{l.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-500"/> : <Circle className="w-4 h-4 text-neutral-300"/>}{l.title}</span>
                <span className="flex items-center gap-2 text-xs text-neutral-400">{l.completed_at && fmtD(l.completed_at)}<Badge>{l.status}</Badge></span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </Panel>
  );
}

function DocumentsTab({ d, storeId, reload }) {
  const [f, setF] = useState({ type: "servsafe", expires_at: "" });
  const onUploaded = async (up) => {
    if (!storeId) return toast.error("Employee has no store assigned");
    try { await api.post(`/stores/${storeId}/documents`, { type: f.type, user_id: d.employee.id, file_url: up.url, filename: up.filename, expires_at: f.expires_at || null }); toast.success("Document saved"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (doc) => { if (!window.confirm("Delete document?")) return; try { await api.delete(`/documents/${doc.id}`); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const expired = (doc) => doc.expires_at && doc.expires_at < new Date().toISOString().slice(0, 10);
  return (
    <>
      <Panel className="p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="emp-doc-form">
        <div><Label>Type</Label><Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="emp-doc-type">{DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></div>
        <div><Label>Expires</Label><Input type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className="h-10"/></div>
        <UploadButton kind="documents" accept="image/*,application/pdf" label="Upload document" onUploaded={onUploaded} testId="emp-doc-upload"/>
      </Panel>
      <Panel className="divide-y divide-neutral-100">
        {d.documents.length === 0 && <p className="p-8 text-center text-sm text-neutral-500" data-testid="emp-docs-empty">No documents on file.</p>}
        {d.documents.map((doc) => (
          <div key={doc.id} className="p-4 flex items-center gap-3" data-testid={`emp-doc-${doc.id}`}>
            <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center">{doc.type === "servsafe" ? <ShieldCheck className="w-4 h-4 text-emerald-600"/> : <FileText className="w-4 h-4"/>}</div>
            <div className="flex-1"><div className="font-medium">{doc.title}</div><div className="text-xs text-neutral-500">{doc.filename} · {fmtD(doc.created_at)}{doc.expires_at && <> · expires {fmtD(doc.expires_at)}</>}</div></div>
            {expired(doc) && <Badge tone="red">expired</Badge>}
            <a href={doc.file_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5"/></Button></a>
            <button onClick={() => remove(doc)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </Panel>
    </>
  );
}

function TimeclockTab({ d }) {
  const hours = (r) => { if (!r.clock_out) return "—"; return ((new Date(r.clock_out) - new Date(r.clock_in)) / 36e5).toFixed(2); };
  return (
    <Panel className="overflow-hidden" data-testid="emp-timeclock">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500"><tr><th className="p-3">Clock in</th><th className="p-3">Clock out</th><th className="p-3 text-right">Hours</th></tr></thead>
        <tbody className="divide-y divide-neutral-100">
          {d.clock_records.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-neutral-500">No time records.</td></tr>}
          {d.clock_records.map((r) => <tr key={r.id || r.clock_in}><td className="p-3">{fmtDT(r.clock_in)}</td><td className="p-3">{r.clock_out ? fmtDT(r.clock_out) : <Badge tone="green">on shift</Badge>}</td><td className="p-3 text-right font-mono">{hours(r)}</td></tr>)}
        </tbody>
      </table>
    </Panel>
  );
}

export default function EmployeeDetail() {
  const { eid, sid } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("tasks");
  const load = useCallback(() => { api.get(`/employees/${eid}/overview`).then((r) => setD(r.data)).catch(() => setD(false)); }, [eid]);
  useEffect(load, [load]);

  if (d === null) return <div className="p-10 text-neutral-500">Loading…</div>;
  if (d === false) return <div className="p-10 text-neutral-500">Employee not found.</div>;
  const e = d.employee;
  const storeId = sid || e.store_id || e.assigned_store_ids?.[0];
  const back = sid ? `/admin/stores/${sid}/employees` : "/admin/employees";

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <Link to={back} className="text-xs text-neutral-500 inline-flex items-center gap-1 mb-4 hover:text-neutral-900" data-testid="back-employees"><ArrowLeft className="w-3 h-3"/>Employees</Link>
      <PageHeader eyebrow={e.role?.replace("_", " ")} title={e.name} testId="emp-detail-name" subtitle={`${e.email}${e.position ? ` · ${e.position}` : ""}`}/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[["Open tasks", d.tasks.filter((t) => t.status === "open").length, "emp-stat-tasks"], ["Training", `${d.training.percent}%`, "emp-stat-training"], ["Documents", d.documents.length, "emp-stat-docs"], ["Shifts logged", d.clock_records.length, "emp-stat-shifts"]].map(([l, v, id]) => (
          <div key={id} className="rounded-xl bg-neutral-50 p-3" data-testid={id}><div className="text-[11px] uppercase tracking-wider text-neutral-500">{l}</div><div className="font-display font-bold text-xl mt-0.5">{v}</div></div>
        ))}
      </div>
      <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1 mb-5 flex-wrap">
        {TABS.map(([k, l, Icon]) => <button key={k} onClick={() => setTab(k)} className={`px-4 h-9 rounded-full text-sm inline-flex items-center gap-1.5 ${tab === k ? "bg-neutral-900 text-white" : "text-neutral-600"}`} data-testid={`emp-tab-${k}`}><Icon className="w-4 h-4"/>{l}</button>)}
      </div>
      {tab === "tasks" && <TasksTab d={d} storeId={storeId} reload={load}/>}
      {tab === "training" && <TrainingTab d={d}/>}
      {tab === "documents" && <DocumentsTab d={d} storeId={storeId} reload={load}/>}
      {tab === "timeclock" && <TimeclockTab d={d} user={user}/>}
    </div>
  );
}
